/**
 * useCommandPalette — tiny shared open/close state for the global ⌘K palette.
 *
 * The palette itself lives in `frontend/src/components/CommandPalette.tsx`
 * and is mounted once in `App.tsx`. The provider wires the global keydown
 * listener (`Cmd/Ctrl+K`) so the shortcut works from any route.
 *
 * Phase 4.5 — initial scope is kontakty search; Phase 12 graduates the
 * palette to multi-source (zákazníci + vozidla + actions).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface CommandPaletteContextValue {
  open: boolean;
  setOpen: (next: boolean) => void;
  toggle: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  const toggle = useCallback(() => setOpen((prev) => !prev), []);

  // Global ⌘K / Ctrl+K listener.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isCmdK) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo<CommandPaletteContextValue>(
    () => ({ open, setOpen, toggle }),
    [open, toggle],
  );

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx)
    throw new Error('useCommandPalette must be used inside <CommandPaletteProvider>');
  return ctx;
}

// Small localStorage-backed recent-picks stack. Multi-type so the palette
// remembers any record the user opened — kontakt, zákazník, or vozidlo.
const RECENT_KEY = 'palette.recent';
const RECENT_MAX = 6;

export type PaletteResultType =
  | 'kontakt'
  | 'zakaznik'
  | 'vozidlo'
  | 'nabidka'
  | 'smlouva';

export interface RecentPick {
  type: PaletteResultType;
  id: number;
  label: string;
  sub?: string | null;
}

export function readRecents(): RecentPick[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is RecentPick =>
        typeof p === 'object' &&
        p != null &&
        typeof p.id === 'number' &&
        typeof p.label === 'string' &&
        (p.type === 'kontakt' ||
          p.type === 'zakaznik' ||
          p.type === 'vozidlo' ||
          p.type === 'nabidka' ||
          p.type === 'smlouva'),
    );
  } catch {
    return [];
  }
}

export function pushRecent(pick: RecentPick): void {
  if (typeof window === 'undefined') return;
  const current = readRecents().filter((p) => !(p.type === pick.type && p.id === pick.id));
  current.unshift(pick);
  const trimmed = current.slice(0, RECENT_MAX);
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(trimmed));
  } catch {
    // Quota / disabled storage — ignore.
  }
}
